import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Spin, Select } from "antd";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { motion } from "framer-motion";
import { AuthContext } from "../../context/AuthContext";

const { Option } = Select;

export default function OrganisationLeave() {
    const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Get current month & year
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState(1);
    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(currentYear);

    const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: {
                Authorization: `Bearer ${authData?.access}`
            }
        })
        console.log("Org : ", res.data.results)
        setOrganisations(res.data.results)
    }

    useEffect(() => {
        fetchOrganisation()
    }, [])

    useEffect(() => {
        const fetchLeaveSummary = async () => {
            setLoading(true);
            try {
                const response = await axios.get(
                    `${backUrl}/api/reports/organisation-leave-summary/?organisation_id=${organisationId}&month=${month}&year=${year}`
                );
                setData(response.data);
            } catch (error) {
                console.error("Error fetching organisation leave summary:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaveSummary();
    }, [organisationId, month, year, backUrl]);

    const COLORS = [
        "#3B82F6", // CL
        "#22C55E", // SL
        "#F59E0B", // EL
        "#EF4444", // LOP
        "#8B5CF6", // Permission
        "#06B6D4", // Halfday
        "#94A3B8", // Sunday
        "#EAB308", // Holiday
    ];

    const chartData = data
        ? [
            { name: "Casual Leave (CL)", value: data.total_cl },
            { name: "Sick Leave (SL)", value: data.total_sl },
            { name: "Earned Leave (EL)", value: data.total_el },
            { name: "LOP", value: data.total_lop },
            { name: "Permission", value: data.total_permission },
            { name: "Half Day", value: data.total_halfday },
            { name: "Sunday", value: data.total_sunday },
            { name: "Holiday", value: data.total_holiday },
        ]
        : [];

    const months = [
        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    // total leaves sum
    const totalLeaves = chartData.reduce((sum, item) => sum + item.value, 0);

    return (
        <div className="p-6 bg-gray-50 min-h-screen flex flex-col justify-start items-center">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-6 justify-center">
                <Select
                    value={organisationId}
                    onChange={(value) => setOrganisationId(value)}
                    style={{ width: 280 }}
                >
                    {organisations.map((item) => (
                        <Option key={item.id} value={item.id}>
                            {item.name}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={month}
                    onChange={(value) => setMonth(value)}
                    style={{ width: 120, marginLeft: '2px' }}
                >
                    {months.map((m, i) => (
                        <Option key={i + 1} value={i + 1}>
                            {m}
                        </Option>
                    ))}
                </Select>

                <Select
                    value={year}
                    onChange={(value) => setYear(value)}
                    style={{ width: 120, marginLeft: '2px' }}
                >
                    {years.map((y) => (
                        <Option key={y} value={y}>
                            {y}
                        </Option>
                    ))}
                </Select>
            </div>

            {loading ? (
                <Spin size="large" />
            ) : (
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Card
                        title={`Organisation Leave Summary — ${months[month - 1]} ${year}`}
                        style={{
                            borderRadius: "16px",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                            textAlign: "center",
                            width: 470,
                            marginTop: 10
                        }}
                    >
                        <ResponsiveContainer width="100%" height={350}>
                            <PieChart>
                                <Pie
                                    data={chartData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={3}
                                    label
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>

                        <div className="mt-4 text-gray-600">
                            <strong>Total Records:</strong> {totalLeaves}
                        </div>
                    </Card>
                </motion.div>
            )}
        </div>
    );
}
